import { ALL_DIAL_CODES } from "../../constants/phoneDialCodes";

function digitsOnly(value: string): string {
  return String(value ?? "").replace(/\D/g, "");
}

/**
 * Split a stored phone value into ITU-T dial code + national subscriber number.
 * When `raw` is E.164 (leading +), the longest matching dial prefix from {@link ALL_DIAL_CODES}
 * wins so values like +9181… are not mis-parsed with a stale fallback dial (e.g. +34).
 */
export function splitDialNationalFromStored(raw: string, fallbackDial: string): { national: string; dial: string } {
  const s = String(raw ?? "").trim();
  const fb = (fallbackDial || "").trim() || "+34";
  if (!s) return { national: "", dial: fb };
  if (!s.startsWith("+")) return { national: s, dial: fb };
  const sorted = [...ALL_DIAL_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const { code } of sorted) {
    if (s.startsWith(code)) {
      return { dial: code, national: s.slice(code.length).trim() };
    }
  }
  return { national: s.replace(/^\+\d+/, "").trim() || s, dial: fb };
}

/**
 * Parse user input/paste into dial + national parts (Login, org signup, representative phone).
 * Supports ``+34…``, ``0034…``, and spaced formats like ``+34 612 345 678``.
 */
export function parsePhoneInputValue(
  raw: string,
  fallbackDial: string
): { dial: string; national: string } {
  const fb = (fallbackDial || "").trim() || "+34";
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return { dial: fb, national: "" };

  const compact = trimmed.replace(/\s+/g, "");
  const e164 = compact.startsWith("+")
    ? compact
    : compact.startsWith("00")
      ? `+${compact.slice(2)}`
      : "";

  if (e164) {
    const { dial, national } = splitDialNationalFromStored(e164, fb);
    return { dial, national: digitsOnly(national) };
  }

  return { dial: fb, national: digitsOnly(compact) };
}
